// Só mostra a opção de frete mais rápida — escolha final fica pro checkout.
// Reaproveita as rotas nativas de assets/cart-consultant-info.js
// (prepare_shipping_rates + polling). Módulo @theme/zrzs-quick-add-shipping.

/** @param {string} cep */
function normalizeCep(cep) {
  return cep.replace(/\D/g, '');
}

/**
 * Normaliza pra "00000-000" — usado tanto no corpo da requisição quanto na
 * exibição do CEP no resultado.
 * @param {string} cep
 */
export function formatCep(cep) {
  const digits = normalizeCep(cep);
  if (digits.length === 8) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return cep.trim();
}

function getCartRoot() {
  const shopify = /** @type {any} */ (window).Shopify;
  return (shopify && shopify.routes && shopify.routes.root) || '/';
}

/** @param {number} ms */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** @param {number | number[] | undefined} deliveryDays */
function representativeDays(deliveryDays) {
  if (deliveryDays == null) return null;
  const value = Array.isArray(deliveryDays)
    ? deliveryDays[deliveryDays.length - 1]
    : deliveryDays;
  const days = Number(value);
  return Number.isNaN(days) ? null : days;
}

/**
 * @param {string[] | undefined} deliveryRange
 * @param {string | undefined} deliveryDate
 */
function representativeDateTimestamp(deliveryRange, deliveryDate) {
  const raw =
    deliveryRange && deliveryRange.length
      ? deliveryRange[deliveryRange.length - 1]
      : deliveryDate;
  if (!raw) return null;
  const timestamp = new Date(raw).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

/**
 * Escolhe a opção de frete mais rápida entre as retornadas: menor data de
 * entrega estimada e, na ausência dela, menor número de dias úteis.
 * @param {any[]} rates
 */
function pickFastestRate(rates) {
  return rates.reduce((fastest, rate) => {
    if (!fastest) return rate;

    const fastestDate = representativeDateTimestamp(
      fastest.delivery_range,
      fastest.delivery_date
    );
    const rateDate = representativeDateTimestamp(
      rate.delivery_range,
      rate.delivery_date
    );

    if (rateDate != null && fastestDate != null) {
      return rateDate < fastestDate ? rate : fastest;
    }
    if (rateDate != null && fastestDate == null) return rate;
    if (rateDate == null && fastestDate != null) return fastest;

    const fastestDays = representativeDays(fastest.delivery_days);
    const rateDays = representativeDays(rate.delivery_days);
    if (rateDays != null && (fastestDays == null || rateDays < fastestDays)) {
      return rate;
    }
    return fastest;
  }, null);
}

/**
 * @param {string} root
 * @param {number} [maxAttempts]
 * @param {number} [delayMs]
 * @param {number} [initialDelayMs]
 */
async function pollShippingRates(
  root,
  maxAttempts = 15,
  delayMs = 800,
  initialDelayMs = 1000
) {
  // Cálculo é async — a primeira chamada costuma vir "waiting" se for cedo demais.
  await sleep(initialDelayMs);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await fetch(`${root}cart/async_shipping_rates.json`, {
      method: 'GET',
      headers: {
        Accept: 'application/json, text/javascript, */*; q=0.01',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'include',
    });

    if (res.ok) {
      const data = await res.json();
      if (!data.waiting && Array.isArray(data.shipping_rates)) {
        return data.shipping_rates;
      }
    }

    if (attempt < maxAttempts - 1) {
      await sleep(delayMs);
    }
  }

  throw new Error('shipping rates timeout');
}

/**
 * Escolhe a opção de frete mais barata entre as retornadas — normalmente a gratuita.
 * @param {any[]} rates
 */
function pickCheapestRate(rates) {
  return rates.reduce((cheapest, rate) => {
    if (!cheapest) return rate;
    const cheapestPrice = parseFloat(cheapest.price);
    const ratePrice = parseFloat(rate.price);
    if (Number.isNaN(ratePrice)) return cheapest;
    if (Number.isNaN(cheapestPrice)) return rate;
    return ratePrice < cheapestPrice ? rate : cheapest;
  }, null);
}

/**
 * @param {string} cep
 * @returns {Promise<{ fastest: any, cheapest: any }>} opção mais rápida e mais barata
 *   retornadas pela API — `cheapest === fastest` (mesma referência) quando só há
 *   uma opção ou quando a mais rápida também é a mais barata.
 */
export async function fetchShippingRates(cep) {
  if (normalizeCep(cep).length !== 8) {
    throw new Error('Informe um CEP válido com 8 dígitos.');
  }

  const root = getCartRoot();
  const body = new URLSearchParams({
    'shipping_address[zip]': formatCep(cep),
    'shipping_address[country]': 'Brazil',
    'shipping_address[province]': 'RJ',
  });

  const prepareRes = await fetch(`${root}cart/prepare_shipping_rates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      Accept: '*/*',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'include',
    body,
  });

  if (!prepareRes.ok) {
    throw new Error('Não foi possível consultar o frete. Tente novamente.');
  }

  const rates = await pollShippingRates(root);
  if (!rates.length) {
    throw new Error('Nenhuma opção de frete disponível para este CEP.');
  }

  return { fastest: pickFastestRate(rates), cheapest: pickCheapestRate(rates) };
}

/** @param {number | string} price */
export function formatShippingPrice(price) {
  const amount = typeof price === 'string' ? parseFloat(price) : price;
  if (Number.isNaN(amount)) return String(price);
  if (amount === 0) return 'grátis';
  return amount.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/** @param {string} dateStr */
function formatDeliveryDate(dateStr) {
  const parts = dateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return dateStr;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Estimate/price separados: o texto de apoio usa text-transform: lowercase,
 * que corromperia o "R$" — `price` deve renderizar isolado.
 * @param {any} rate
 * @returns {{ estimate: string, price: string }}
 */
export function getShippingEstimateLabel(rate) {
  const finalDate =
    rate.delivery_range && rate.delivery_range.length
      ? rate.delivery_range[rate.delivery_range.length - 1]
      : rate.delivery_date;

  const price = formatShippingPrice(rate.price != null ? rate.price : 0);

  if (finalDate) {
    return { estimate: `chega até ${formatDeliveryDate(finalDate)}`, price };
  }

  const days = representativeDays(rate.delivery_days);
  if (days != null) {
    const daysLabel = days === 1 ? 'dia útil' : 'dias úteis';
    return { estimate: `chega em ${days} ${daysLabel}`, price };
  }

  return { estimate: '', price };
}
